import { useState, useEffect } from 'react';
import { CheckCircle2, Monitor, Cpu, Globe, Clock, ChevronRight, RefreshCw } from 'lucide-react';
import useSiteSetupStore from '../../../stores/siteSetupStore';
import fetchWithTenant from '../../../utils/fetchWithTenant';

const AgentVerifyStep = ({ onNext }) => {
  const { markStepComplete } = useSiteSetupStore();
  const [agent, setAgent] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadAgent();
  }, []);

  const loadAgent = async () => {
    setLoading(true);
    try {
      const res = await fetchWithTenant('/api/md/agents');
      if (res.ok) {
        const data = await res.json();
        const agents = data.agents || data || [];
        const connected = agents.find(a => a.status === 'CONNECTED') || agents[0];
        setAgent(connected || null);
      }
    } catch (err) {
      console.error('[AgentVerify] Failed to load agent:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleContinue = async () => {
    await markStepComplete('agent_verify', {
      agentId: agent?.id,
      agentHostname: agent?.hostname
    });
    onNext();
  };

  if (loading) {
    return (
      <div className="site-setup-step agent-verify-step">
        <div className="verify-loading">
          <RefreshCw size={24} className="spin" />
          <p>Loading agent details...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="site-setup-step agent-verify-step">
      <h3>Agent Verified</h3>
      <p className="step-description">Your agent is connected and ready. Here are the details:</p>

      {agent ? (
        <div className="agent-detail-card">
          <div className="agent-detail-status">
            <CheckCircle2 size={32} />
            <span>Connected</span>
          </div>

          <div className="agent-detail-grid">
            <div className="agent-detail-row">
              <Monitor size={16} />
              <span className="agent-detail-label">Hostname</span>
              <strong>{agent.hostname || 'Unknown'}</strong>
            </div>
            <div className="agent-detail-row">
              <Cpu size={16} />
              <span className="agent-detail-label">Version</span>
              <strong>{agent.version || agent.version || 'Unknown'}</strong>
            </div>
            <div className="agent-detail-row">
              <Globe size={16} />
              <span className="agent-detail-label">Operating System</span>
              <strong>{agent.osType || 'Unknown'}</strong>
            </div>
            <div className="agent-detail-row">
              <Globe size={16} />
              <span className="agent-detail-label">IP Address</span>
              <strong>{agent.localIp || 'Unknown'}</strong>
            </div>
            <div className="agent-detail-row">
              <Clock size={16} />
              <span className="agent-detail-label">Last Heartbeat</span>
              <strong>{agent.lastSeenAt ? new Date(agent.lastSeenAt).toLocaleString() : 'Just now'}</strong>
            </div>
          </div>
        </div>
      ) : (
        <div className="agent-detail-card error">
          <p>No agent found. Go back and complete the agent setup.</p>
        </div>
      )}

      <div className="step-actions">
        <button className="site-setup-btn primary" onClick={handleContinue} disabled={!agent}>
          Continue to Network Setup <ChevronRight size={16} />
        </button>
      </div>
    </div>
  );
};

export default AgentVerifyStep;
